import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { auth, db } from './shared/storage/firebase';
import { doc, updateDoc, increment, onSnapshot } from 'firebase/firestore';
import { Muse, ProtectedRouteProps, LayoutProps, Squad } from './types';
import { AuthProvider, useAuth } from './domains/auth/contexts/AuthContext';
import { StudentAuthService } from './shared/storage/StudentAuthService';
import { ThemeProvider, useTheme } from './shared/contexts/ThemeContext';
import { TeacherToolsProvider } from './shared/contexts/TeacherToolsContext';
import StudentReflectionListener from './domains/progress/components/StudentReflectionListener';
import StudentGameLaunchListener from './domains/games/components/StudentGameLaunchListener';
import SongNamingModal from './domains/games/components/SongNamingModal';
import { usesLegacyMusicDashboard } from './domains/squads/utils/curriculum';
import { firstAdminTab, hasAnyAdminPermission } from './domains/auth/adminPermissions';
import { hasFullPlatformStaffStatus, hasOnboardingHomeScheduleAccess, isHomeSchedulePath } from './domains/auth/teacherAccess';

// Page imports (thin wrappers)
import LoginPage from './pages/Login';
import PickMusePage from './pages/PickMuse';
import MuseMarketPage from './pages/MuseMarket';
import PackOpeningPage from './pages/PackOpening';
import MyMusesPage from './pages/MyMuses';
import GamesPage from './pages/Games';
import SavedSongsPage from './pages/SavedSongs';
import AdminPanelPage from './pages/AdminPanel';
import AdminOpsPage from './pages/AdminOps';
import AdminInvoicePage from './pages/AdminInvoice';
import AdminInvoiceTemplatePage from './pages/AdminInvoiceTemplate';
import AdminRecordPaymentPage from './pages/AdminRecordPayment';
import AdminReferredSchoolsPage from './pages/AdminReferredSchools';
import SquadsPage from './pages/SquadsPage';
import SquadDashboardPage from './pages/SquadDashboardPage';
import MissionDashboardPage from './pages/MissionDashboardPage';
import FullScreenCodePage from './pages/FullScreenCodePage';
import FullScreenGamePage from './pages/FullScreenGamePage';
import ResourcesPage from './pages/ResourcesPage';
import CertificationDetailPage from './pages/CertificationDetailPage';
import TrainingDetailPage from './pages/TrainingDetailPage';
import ParentCreateAccount from './pages/ParentCreateAccount';
import ParentDashboardPage from './pages/ParentDashboard';
import AuthActionPage from './pages/AuthAction';
import AdminParentPreviewPage from './pages/AdminParentPreview';
import AdminTeacherDetailPage from './pages/AdminTeacherDetail';
import StudentLaunchPage from './pages/StudentLaunch';
import Terms from './pages/Terms';
import PhotoRelease from './pages/PhotoRelease';
import OGCreateTeacherPage from './pages/OGCreateTeacherPage';
import StaffHomePage from './pages/StaffHome';
import AvailabilityPage from './pages/Availability';
import AdminTeacherAvailabilityPage from './pages/AdminTeacherAvailability';
import AdminTeacherMapPage from './pages/AdminTeacherMap';
import TeacherProfilePage from './pages/TeacherProfile';
import TeacherOnboardingPage from './pages/TeacherOnboardingPage';
import OfferAcceptPage from './pages/OfferAcceptPage';
import AdminHiringTemplatesPage from './pages/AdminHiringTemplates';
import ClaimSubLandingPage from './pages/ClaimSubLanding';
import ClaimSubPage from './pages/ClaimSub';
import BillingPortalPage from './pages/BillingPortal';
import SquadDetailsPage from './pages/SquadDetailsPage';
import AdminSquadDetailsPage from './pages/AdminSquadDetailsPage';
import AdminPartnersPage from './pages/AdminPartners';
import AdminPartnerDetailPage from './pages/AdminPartnerDetail';
import LibraryPage from './pages/Library';

// Shared UI components
import Sidebar from './shared/ui/Sidebar';
import TeacherNavigation from './shared/ui/TeacherNavigation';
import DashboardWallpaper from './domains/squads/components/classroom/DashboardWallpaper';
import AchievementNotification from './shared/ui/AchievementNotification';
import './shared/styles/globals/App.css';
import './shared/styles/components/UserProfile.css';
import './domains/muses/styles/MuseTiers.css';
import DemoBoot from './domains/demo/DemoBoot';
import VirtualPianoKeyboard from './domains/games/components/VirtualPianoKeyboard';

const isTeacherOnboardingPath = (pathname: string) =>
  pathname === '/app/onboarding'
  || pathname === '/app/profile'
  || pathname === '/app/availability'
  || pathname.startsWith('/app/offer')
  || pathname === '/app/resources'
  || pathname.startsWith('/app/resources/');


const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { currentUser, loading } = useAuth();
  if (loading) {
    return <div>Loading...</div>; // Or a spinner component
  }
  if (!currentUser) {
    return <Navigate to="/" />;
  }
  return children;
};

const TeacherProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { currentUser, userData, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div>Loading...</div>; // Or a proper loading screen
  }

  if (!currentUser) {
    return <Navigate to="/" />;
  }

  if (userData?.role !== 'teacher') {
    return <Navigate to="/app/games" replace />; // Or an unauthorized page
  }

  const staffStatus = userData.staffStatus ?? 'active';
  const hiringStatus = String((userData as any).hiringStatus || '');
  const onboardingInProgress = ['offer_sent', 'offer_accepted', 'onboarding'].includes(hiringStatus);
  const isRestrictedTeacher = userData.isAdmin !== true && (!hasFullPlatformStaffStatus(staffStatus) || onboardingInProgress);
  const pendingStateCheckAccess = hasOnboardingHomeScheduleAccess(userData)
    && isHomeSchedulePath(location.pathname);
  if (isRestrictedTeacher && !pendingStateCheckAccess && !isTeacherOnboardingPath(location.pathname)) {
    return <Navigate to="/app/onboarding" replace />;
  }

  return children;
};

const TeacherStaffGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { userData, loading } = useAuth();
  const location = useLocation();

  if (loading) return <div>Loading...</div>;
  if (!userData) return <>{children}</>;

  const staffStatus = userData.staffStatus ?? 'active';
  const hiringStatus = String((userData as any).hiringStatus || '');
  const onboardingInProgress = ['offer_sent', 'offer_accepted', 'onboarding'].includes(hiringStatus);
  const isRestrictedTeacher = userData.role === 'teacher'
    && userData.isAdmin !== true
    && (!hasFullPlatformStaffStatus(staffStatus) || onboardingInProgress);
  if (!isRestrictedTeacher) return <>{children}</>;
  if (isTeacherOnboardingPath(location.pathname)
    || (hasOnboardingHomeScheduleAccess(userData) && isHomeSchedulePath(location.pathname))) return <>{children}</>;
  return <Navigate to="/app/onboarding" replace />;
};

const PartnerGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { userData, loading } = useAuth();
  const location = useLocation();

  if (loading) return <div>Loading...</div>;
  if (!userData) return <>{children}</>;

  if (userData.role !== 'partner') return <>{children}</>;

  // Partners are preview-only accounts:
  // Allow: Games, Creations, Muse Vault, My Muses (+ pack opening as part of the Vault flow)
  const p = location.pathname;
  const allowed =
    p === '/app' ||
    p === '/app/' ||
    p === '/app/games' ||
    p.startsWith('/app/games/') ||
    p === '/app/songs' ||
    p === '/app/market' ||
    p === '/app/mymuses' ||
    p === '/app/pack';

  if (!allowed) {
    return <Navigate to="/app/games" replace />;
  }

  return <>{children}</>;
};

const AppIndexRedirect: React.FC = () => {
  const { userData, loading } = useAuth();
  if (loading) return <div>Loading...</div>;

  if (userData?.role === 'teacher') {
    const staffStatus = userData.staffStatus ?? 'active';
    const hiringStatus = String((userData as any).hiringStatus || '');
    if (userData.isAdmin !== true
      && (!hasFullPlatformStaffStatus(staffStatus) || ['offer_sent', 'offer_accepted', 'onboarding'].includes(hiringStatus))) {
      if (hasOnboardingHomeScheduleAccess(userData)) return <Navigate to="squads" replace />;
      return <Navigate to="onboarding" replace />;
    }
    // Admin default landing: Admin panel → Todos
    if (userData.isAdmin === true) {
      return <Navigate to="admin" replace state={{ adminTab: 'todos' }} />;
    }
    if (hasAnyAdminPermission(userData)) {
      return <Navigate to="admin" replace state={{ adminTab: firstAdminTab(userData) }} />;
    }
    return <Navigate to="squads" replace />;
  }

  if (userData?.role === 'parent') {
    return <Navigate to="students" replace />;
  }

  return <Navigate to="games" replace />;
};

const AdminProtectedRoute: React.FC<ProtectedRouteProps & { allowDelegated?: boolean }> = ({ children, allowDelegated = false }) => {
  const { currentUser, userData, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>; // Or a proper loading screen
  }

  if (!currentUser) {
    return <Navigate to="/" />;
  }

  if (userData?.isAdmin !== true && !(allowDelegated && hasAnyAdminPermission(userData))) {
    return <Navigate to="/app/squads" />; // Redirect non-admins to squads page
  }

  return children;
};

const ParentProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { currentUser, userData, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>; // Or a proper loading screen
  }

  if (!currentUser) {
    return <Navigate to="/" />;
  }

  if (userData?.role !== 'parent') {
    return <Navigate to="/app/games" />; // Redirect non-parents to games page
  }

  return children;
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const { currentUser, userData } = useAuth();
  const { autumnMode } = useTheme();
  const [selectedMuse, setSelectedMuse] = useState<Muse | null>(null);
  const [museCash, setMuseCash] = useState<number>(0);
  const [stars, setStars] = useState<number>(0);
  const [lastReward, setLastReward] = useState<number>(0);
  const [lastStarReward, setLastStarReward] = useState<number>(0);
  const [currentAchievement, setCurrentAchievement] = useState<any>(null);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isBlizzardActive, setIsBlizzardActive] = useState(false);
  const [activeTagAnimation, setActiveTagAnimation] = useState<string | null>(null);
  const [areEggsExpanded, setAreEggsExpanded] = useState(false);
  const [showPianoKeyboard, setShowPianoKeyboard] = useState(false);
  const [isMusicClassOpenOnDashboard, setIsMusicClassOpenOnDashboard] = useState(false);
  const [isMusicSquadDashboard, setIsMusicSquadDashboard] = useState(false);
  const [loadedSquadDashboardId, setLoadedSquadDashboardId] = useState<string | null>(null);
  const tagAnimationTimeoutRef = useRef<number | null>(null);
  const [songNamingData, setSongNamingData] = useState<{ eventId: string; audioUrl?: string } | null>(null);

  useEffect(() => {
    if (userData) {
      setSelectedMuse(userData.selectedMuse);
      // Only set museCash if we have actual data, not on initial load
      if (userData.museCash !== undefined) {
        setMuseCash(userData.museCash);
      }
      // Set stars with default value if undefined
      if (userData.stars !== undefined) {
        setStars(userData.stars);
      } else {
        setStars(0);
      }
    }
  }, [userData]);

  // Keep latest userData in a ref so the handler doesn't need to re-register
  const userDataRef = useRef(userData);
  useEffect(() => { userDataRef.current = userData; }, [userData]);

  useEffect(() => {
    // Use window-scoped singletons so HMR/StrictMode don't create duplicates
    const w = window as any;
    const HANDLER_KEY = '__ogAchievementHandler';
    const PROCESSED_KEY = '__ogProcessedAchievementIds';

    if (!w[PROCESSED_KEY]) {
      w[PROCESSED_KEY] = new Set<string>();
    }
    const processedIds: Set<string> = w[PROCESSED_KEY];

    const handleAchievementUnlock = async (event: CustomEvent) => {
      console.log('🎊 ACHIEVEMENT EVENT RECEIVED:', event.detail);
      const { achievement, reward, eventId } = event.detail || {};
      const rewardNum = Number((reward ?? achievement?.museCashReward ?? 0));
      if (!Number.isFinite(rewardNum) || rewardNum <= 0) {
        // Still show the achievement toast even if reward is missing/invalid.
        setCurrentAchievement(achievement);
        setLastReward(0);
        return;
      }

      // Dedupe by stable eventId if provided
      if (eventId && processedIds.has(eventId)) {
        return;
      }
      if (eventId) {
        processedIds.add(eventId);
        try {
          const ssKey = `og_achv_${eventId}`;
          if (sessionStorage.getItem(ssKey) === '1') {
            return;
          }
          sessionStorage.setItem(ssKey, '1');
        } catch (_) {
          // ignore storage errors
        }
      }

      setCurrentAchievement(achievement);
      setLastReward(rewardNum);
      
      // Update muse cash immediately in local state for instant UI feedback
      setMuseCash(prev => (Number.isFinite(prev) ? prev : 0) + rewardNum);
      console.log(`💰 MUSE CASH +${rewardNum}`);
      
      // Persist to Firebase asynchronously using atomic increment (avoid races)
      setTimeout(async () => {
        try {
          const latestUser = userDataRef.current;
          if (auth.currentUser && latestUser) {
            const userRef = doc(db, 'users', latestUser.id);
            await updateDoc(userRef, { museCash: increment(rewardNum) });
            console.log(`💾 MUSE CASH INCREMENTED IN FIREBASE BY: ${rewardNum}`);
          }
        } catch (error) {
          console.error('Error syncing muse cash to Firebase:', error);
        }
      }, 100); // Small delay to not block the UI update
    };

    // Stable handler registration (singleton)
    const eventHandler = (event: Event) => handleAchievementUnlock(event as CustomEvent);
    if (w[HANDLER_KEY]) {
      window.removeEventListener('achievementUnlocked', w[HANDLER_KEY]);
    }
    w[HANDLER_KEY] = eventHandler;
    window.addEventListener('achievementUnlocked', w[HANDLER_KEY]);
    
    return () => {
      // Keep the singleton alive across transient unmounts; however, if this instance
      // owns the current handler reference, remove it to avoid leaks on real unmount
      if (w[HANDLER_KEY] === eventHandler) {
        window.removeEventListener('achievementUnlocked', w[HANDLER_KEY]);
        w[HANDLER_KEY] = undefined;
      }
    };
  }, []);

  // Trigger a short blizzard animation whenever Blizzard Beats mode is turned on
  useEffect(() => {
    let timeoutId: number | undefined;
    if (autumnMode) {
      setIsBlizzardActive(true);
      timeoutId = window.setTimeout(() => setIsBlizzardActive(false), 2500);
    }
    return () => {
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [autumnMode]);

  // Trigger a short full-screen animation whenever a name tag is equipped
  useEffect(() => {
    const handleTagEquipped = (event: CustomEvent) => {
      const key = (event.detail && (event.detail as any).animationKey) || event.detail?.animationKey;
      if (!key) return;

      setActiveTagAnimation(key);

      // Clear any existing timeout to avoid overlap
      if (tagAnimationTimeoutRef.current !== null) {
        window.clearTimeout(tagAnimationTimeoutRef.current);
      }

      tagAnimationTimeoutRef.current = window.setTimeout(() => {
        setActiveTagAnimation(null);
        tagAnimationTimeoutRef.current = null;
      }, 2200);
    };

    const eventHandler = (event: Event) => handleTagEquipped(event as CustomEvent);
    window.addEventListener('tagEquipped', eventHandler);

    return () => {
      window.removeEventListener('tagEquipped', eventHandler);
      if (tagAnimationTimeoutRef.current !== null) {
        window.clearTimeout(tagAnimationTimeoutRef.current);
      }
    };
  }, []);

  // Listen for song naming events
  useEffect(() => {
    const handleSongNeedsNaming = (event: CustomEvent) => {
      console.log('🎵 Song needs naming event received:', event.detail);
      console.log('🎵 Audio URL:', event.detail.audioUrl);
      setSongNamingData(event.detail);
    };

    const eventHandler = (event: Event) => handleSongNeedsNaming(event as CustomEvent);
    window.addEventListener('songNeedsNaming', eventHandler);

    return () => {
      window.removeEventListener('songNeedsNaming', eventHandler);
    };
  }, []);

  const handleSongNameSave = (songName: string) => {
    if (songNamingData) {
      console.log('🎵 Song named by user:', songName);
      // Emit event back to GameAPI with the chosen name
      window.dispatchEvent(new CustomEvent('songNamed', {
        detail: {
          eventId: songNamingData.eventId,
          songName: songName
        }
      }));
      setSongNamingData(null);
    }
  };

  const handleSongNameSkip = () => {
    if (songNamingData) {
      console.log('🎵 Song naming skipped');
      // Use default name if skipped
      window.dispatchEvent(new CustomEvent('songNamed', {
        detail: {
          eventId: songNamingData.eventId,
          songName: 'My Song'
        }
      }));
      setSongNamingData(null);
    }
  };

  const getTagParticleEmojis = (key: string): string[] => {
    switch (key) {
      case 'leafy-lander':
        return ['🍁', '🍂', '🍃'];
      case 'composer-trainee':
        return ['🎵', '🎶', '🎼'];
      case 'harmony-hero':
        return ['✨', '🌈', '💫'];
      case 'melody-master':
        return ['🎹', '🎤', '🎧'];
      default:
        return ['✨'];
    }
  };

  const isMissionDashboardRoute =
    location.pathname.startsWith('/app/squads/') &&
    location.pathname.includes('/mission/');
  const isTeacherAvailabilityRoute =
    location.pathname === '/app/availability';

  const isAdminTeacherAvailabilityRoute =
    location.pathname === '/app/admin/teacher-availability';
  const isAdminTeacherMapRoute =
    location.pathname === '/app/admin/teacher-map';
  const isAdminTeacherDetailRoute =
    location.pathname.startsWith('/app/admin/teachers/');

  // Admin panel should be fullscreen with hamburger menu
  const isAdminPanelRoute = location.pathname === '/app/admin';
  const isAnyAdminRoute = location.pathname.startsWith('/app/admin');

  // Squad dashboard only (not /details or /mission/... nested routes)
  const squadDashboardMatch = location.pathname.match(/^\/app\/squads\/([^/]+)$/);
  const squadDashboardId = squadDashboardMatch?.[1] || null;

  // Piano easter egg: only while a music class is open on the squad dashboard
  useEffect(() => {
    if (!squadDashboardId) {
      setIsMusicClassOpenOnDashboard(false);
      setIsMusicSquadDashboard(false);
      setLoadedSquadDashboardId(null);
      return;
    }

    const unsubscribe = onSnapshot(
      doc(db, 'squads', squadDashboardId),
      (snap) => {
        if (!snap.exists()) {
          setIsMusicClassOpenOnDashboard(false);
          setIsMusicSquadDashboard(false);
          setLoadedSquadDashboardId(squadDashboardId);
          return;
        }
        const squad = { id: snap.id, ...snap.data() } as Squad;
        const isMusic = usesLegacyMusicDashboard(squad);
        const isLive =
          squad.classStatus === 'current' && !!(squad as any).liveSession;
        setIsMusicSquadDashboard(isMusic);
        setIsMusicClassOpenOnDashboard(isLive && isMusic);
        setLoadedSquadDashboardId(squadDashboardId);
      },
      () => {
        setIsMusicClassOpenOnDashboard(false);
        // Preserve the legacy shell when curriculum cannot be verified.
        setIsMusicSquadDashboard(true);
        setLoadedSquadDashboardId(squadDashboardId);
      }
    );

    return () => unsubscribe();
  }, [squadDashboardId]);

  useEffect(() => {
    if (!isMusicClassOpenOnDashboard) {
      setShowPianoKeyboard(false);
      setAreEggsExpanded(false);
    }
  }, [isMusicClassOpenOnDashboard]);

  // Billing admin pages should be fullscreen (no main app sidebar).
  const isAdminInvoiceTemplateRoute = location.pathname === '/app/admin/invoice-template';
  const isAdminInvoiceDetailRoute = location.pathname.startsWith('/app/admin/invoices/');
  const isAdminRecordPaymentRoute = location.pathname === '/app/admin/record-payment';
  const isAdminOpsRoute = location.pathname.startsWith('/app/admin/ops');
  const isAdminPartnerRoute = location.pathname.startsWith('/app/admin/partners');
  const isTeacherSquadDetailsRoute =
    location.pathname.startsWith('/app/squads/') &&
    location.pathname.endsWith('/details');
  const isAdminSquadDetailsRoute = location.pathname.startsWith('/app/admin/squads/');
  const isBillingFullscreenRoute =
    isAdminTeacherAvailabilityRoute
    || isAdminTeacherMapRoute
    || isAdminTeacherDetailRoute
    || isAdminInvoiceTemplateRoute
    || isAdminInvoiceDetailRoute
    || isAdminRecordPaymentRoute
    || isAdminOpsRoute
    || isAdminPartnerRoute;
  const isFullscreenMainContentRoute =
    isTeacherAvailabilityRoute ||
    isAdminPanelRoute ||
    isBillingFullscreenRoute ||
    isTeacherSquadDetailsRoute ||
    isAdminSquadDetailsRoute;
  const isTeacherAccount = userData?.role === 'teacher';
  const isRestrictedTeacher = isTeacherAccount
    && userData?.isAdmin !== true
    && (
      !hasFullPlatformStaffStatus(userData?.staffStatus)
      || ['offer_sent', 'offer_accepted', 'onboarding'].includes(String((userData as any)?.hiringStatus || ''))
    );
  const isTeacherHomeRoute =
    (location.pathname === '/app/squads'
      && !['calendar', 'map', 'squads', 'team'].includes(String(new URLSearchParams(location.search).get('view') || '').toLowerCase()))
    || location.pathname === '/app/home'
    || (isRestrictedTeacher && location.pathname === '/app/onboarding');
  const useLegacyMusicTeacherSidebar = !!(
    isTeacherAccount
    && squadDashboardId
    && loadedSquadDashboardId === squadDashboardId
    && isMusicSquadDashboard
  );
  // Squad dashboards that are not verified legacy music experiences—including
  // demos and AI classes—use the modern teacher rail. This also gives teacher-
  // admins the same demo shell instead of falling back to the legacy sidebar.
  const useModernTeacherNavigation = (isTeacherAccount || Boolean(squadDashboardId))
    && !useLegacyMusicTeacherSidebar;
  const hasWorkspaceWallpaper = useModernTeacherNavigation && (
    location.pathname === '/app/squads' || isTeacherHomeRoute || location.pathname === '/app/games' || location.pathname === '/app/profile'
  );


  const handleMuseSelect = async (muse: Muse): Promise<void> => {
    if (userData) {
      const userRef = doc(db, 'users', userData.id);
      await updateDoc(userRef, { selectedMuse: muse });
    }
  };

  const onLogout = async (): Promise<void> => {
    try {
      // Check if this is an anonymous student user
      if (currentUser?.isAnonymous) {
        await StudentAuthService.signOut();
      } else {
        await auth.signOut();
      }
    } catch (error) {
      console.error('Error during logout:', error);
      // Force logout even if there's an error
      await auth.signOut();
    }
  };


  return (
    <div
      className={
        useModernTeacherNavigation
          ? `teacher-shell ${isTeacherHomeRoute ? 'teacher-shell--expanded' : 'teacher-shell--rail'}`
          : undefined
      }
      style={{
      display: 'flex',
      minHeight: '100vh',
      fontFamily: 'Fredoka One, Arial Rounded MT Bold, Arial, sans-serif',
      background: useModernTeacherNavigation || isBillingFullscreenRoute
        ? '#ffffff'
        : (autumnMode ? '#ffffff' : '#e3f4fd'),
      position: 'relative'
    }}
    >
      {useModernTeacherNavigation && (
        <TeacherNavigation
          userData={userData}
          onLogout={onLogout}
          isHome={isTeacherHomeRoute}
        />
      )}
      {/* Blizzard Beats snowflake blizzard */}
      {!useModernTeacherNavigation && isBlizzardActive && createPortal(
        <div
          className="blizzard-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            pointerEvents: 'none',
            overflow: 'hidden',
            zIndex: 1200,
            contain: 'paint'
          }}
        >
          {Array.from({ length: 60 }).map((_, i) => (
            <span
              key={i}
              className="snowflake"
              style={{
                position: 'absolute',
                top: '-10%',
                left: `${Math.random() * 100}%`,
                fontSize: `${12 + Math.random() * 18}px`,
                opacity: 0.6 + Math.random() * 0.4,
                animationName: 'snowFall',
                animationDuration: `${1.6 + Math.random() * 1.2}s`,
                animationTimingFunction: 'linear',
                animationIterationCount: 1,
                animationDelay: `${Math.random() * 0.5}s`
              }}
            >
              ❄️
            </span>
          ))}
        </div>,
        document.body
      )}
      {/* Full-screen tag equip celebration, similar strategy to Blizzard overlay */}
      {activeTagAnimation && createPortal(
        <div
          style={{
            position: 'fixed',
            inset: 0,
            pointerEvents: 'none',
            overflow: 'hidden',
            zIndex: 1210,
            contain: 'paint'
          }}
        >
          {Array.from({ length: 50 }).map((_, i) => {
            const emojis = getTagParticleEmojis(activeTagAnimation);
            const emoji = emojis[Math.floor(Math.random() * emojis.length)];
            return (
              <span
                key={i}
                style={{
                  position: 'absolute',
                  top: '-10%',
                  left: `${Math.random() * 100}%`,
                  fontSize: `${14 + Math.random() * 18}px`,
                  opacity: 0.7 + Math.random() * 0.3,
                  animationName: 'snowFall',
                  animationDuration: `${1.4 + Math.random() * 0.8}s`,
                  animationTimingFunction: 'linear',
                  animationIterationCount: 1,
                  animationDelay: `${Math.random() * 0.3}s`
                }}
              >
                {emoji}
              </span>
            );
          })}
        </div>,
        document.body
      )}
      {/* Easter egg tool bubbles (top-right) — only during open music classes on the squad dashboard */}
      {!isAnyAdminRoute && isMusicClassOpenOnDashboard && (
      <div
        style={{
          position: 'fixed',
          top: 18,
          right: 22,
          zIndex: 1400,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: '0.5rem',
          pointerEvents: 'none'
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: '0.35rem',
            pointerEvents: 'auto',
            alignItems: 'center'
          }}
        >
          {/* Expanded tool bubbles */}
          {areEggsExpanded && (
            <>
              {/* Piano easter egg bubble */}
              <button
                type="button"
                onClick={() => setShowPianoKeyboard(prev => !prev)}
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: '999px',
                  border: '3px solid rgba(255,255,255,0.85)',
                  background: 'radial-gradient(circle at 30% 20%, #ffffff, #4fc3f7)',
                  boxShadow: '0 8px 18px rgba(0,0,0,0.35)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontSize: '1.4rem'
                }}
                title="Secret Piano"
              >
                🎹
              </button>
              {/* Placeholder for future easter eggs */}
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: '999px',
                  border: '2px dashed rgba(255,255,255,0.7)',
                  background: 'rgba(255,255,255,0.08)',
                  boxShadow: '0 4px 10px rgba(0,0,0,0.25)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.8rem',
                  color: 'rgba(255,255,255,0.9)'
                }}
              >
                ?
              </div>
            </>
          )}
          {/* Expander bubble */}
          <button
            type="button"
            onClick={() => setAreEggsExpanded(prev => !prev)}
            style={{
              width: 40,
              height: 40,
              borderRadius: '999px',
              border: '3px solid rgba(255,255,255,0.85)',
              background: 'radial-gradient(circle at 20% 20%, #ffffff, #90caf9)',
              boxShadow: '0 6px 14px rgba(0,0,0,0.35)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              padding: 0
            }}
            title={areEggsExpanded ? 'Hide tools' : 'Show tools'}
          >
            <span
              style={{
                display: 'block',
                fontSize: '1.3rem',
                color: '#0d47a1',
                transform: areEggsExpanded ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.18s ease'
              }}
            >
              ‹
            </span>
          </button>
        </div>
      </div>
      )}
      {/* Draggable MIDI piano keyboard */}
      {isMusicClassOpenOnDashboard && showPianoKeyboard && (
        <VirtualPianoKeyboard
          initialLeft={Math.max(40, (window.innerWidth - 800) / 2)}
          initialTop={Math.max(40, (window.innerHeight - 280) / 2)}
          onClose={() => setShowPianoKeyboard(false)}
        />
      )}
      {/* Sidebar and mobile menu are hidden on full-screen routes */}
      {!useModernTeacherNavigation && !(isMissionDashboardRoute || isTeacherAvailabilityRoute || isAdminTeacherAvailabilityRoute || isAdminTeacherMapRoute || isAdminTeacherDetailRoute || isAdminPanelRoute || isAdminInvoiceTemplateRoute || isAdminInvoiceDetailRoute || isAdminRecordPaymentRoute || isAdminOpsRoute || isAdminPartnerRoute || isTeacherSquadDetailsRoute || isAdminSquadDetailsRoute) && (
        <>
          {/* Mobile Menu Overlay */}
          {isMobileSidebarOpen && (
            <div 
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 998,
                display: 'block'
              }}
              onClick={() => setIsMobileSidebarOpen(false)}
            />
          )}

          {/* Hamburger Menu Button */}
          <button
            style={{
              position: 'fixed',
              top: '20px',
              left: '20px',
              zIndex: 1002,
              background: autumnMode
                ? 'linear-gradient(135deg, #cfd9df, #e2ebf0)'
                : '#4FC3F7',
              border: 'none',
              borderRadius: '8px',
              padding: '12px',
              cursor: 'pointer',
              boxShadow: autumnMode
                ? '0 2px 8px rgba(158, 158, 158, 0.4)'
                : '0 2px 8px rgba(79, 195, 247, 0.3)',
              display: 'none',
              flexDirection: 'column',
              gap: '3px',
              width: '44px',
              height: '44px',
              justifyContent: 'center',
              alignItems: 'center'
            }}
            onClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
            className="hamburger-menu"
          >
            <div style={{
              width: '20px',
              height: '2px',
              backgroundColor: '#fff',
              borderRadius: '1px',
              transition: 'all 0.3s ease',
              transform: isMobileSidebarOpen ? 'rotate(45deg) translate(5px, 5px)' : 'none'
            }} />
            <div style={{
              width: '20px',
              height: '2px',
              backgroundColor: '#fff',
              borderRadius: '1px',
              transition: 'all 0.3s ease',
              opacity: isMobileSidebarOpen ? 0 : 1
            }} />
            <div style={{
              width: '20px',
              height: '2px',
              backgroundColor: '#fff',
              borderRadius: '1px',
              transition: 'all 0.3s ease',
              transform: isMobileSidebarOpen ? 'rotate(-45deg) translate(7px, -6px)' : 'none'
            }} />
          </button>

          {/* Sidebar */}
          <div
            style={{
              transform: isMobileSidebarOpen ? 'translateX(0)' : (window.innerWidth <= 900 ? 'translateX(-100%)' : 'translateX(0)'),
              transition: 'transform 0.3s ease',
              zIndex: 999,
              position: 'fixed',
              top: 0,
              left: 0,
              height: '100vh',
              width: window.innerWidth <= 900 ? '260px' : '220px'
            }}
            className="sidebar-container"
          >
            <Sidebar
              selectedMuse={selectedMuse}
              museCash={museCash}
              stars={stars}
              lastReward={lastReward}
              lastStarReward={lastStarReward}
              onLastRewardComplete={() => setLastReward(0)}
              onLastStarRewardComplete={() => setLastStarReward(0)}
              onLogout={onLogout}
              isMobile={window.innerWidth <= 900}
              autumnMode={autumnMode}
            />
          </div>
        </>
      )}

      {/* Main Content */}
      <div
        style={{
          flex: '1 1 auto',
          minWidth: 0,
          position: 'relative',
          minHeight: '100vh',
          transition: 'margin 0.3s ease'
        }}
        className={
          isMissionDashboardRoute
            ? 'main-content mission-main-content'
            : (isFullscreenMainContentRoute)
              ? 'main-content fullscreen-main-content'
              : 'main-content'
        }
      >
       
        {hasWorkspaceWallpaper ? (
          <DashboardWallpaper scope="teacher" scopeId={currentUser?.uid || ''} defaultWallpaper="universe">
            <main className="page-content">{children}</main>
          </DashboardWallpaper>
        ) : <main className="page-content">{children}</main>}
        
        {createPortal(
          <AchievementNotification
            achievement={currentAchievement}
            onComplete={() => setCurrentAchievement(null)}
          />,
          document.body
        )}

        {songNamingData && createPortal(
          <SongNamingModal
            onSave={handleSongNameSave}
            onSkip={handleSongNameSkip}
            audioData={songNamingData.audioUrl}
          />,
          document.body
        )}

        <StudentReflectionListener />
        <StudentGameLaunchListener />
        
        {/* Muse Click Animation Styles */}
        <style>{`
          ${autumnMode ? `
            /* Blizzard Beats Mode Overrides - Apply crisp white background globally */
            .games-page,
            .games-container,
            .squads-page,
            .muses-page,
            .songs-page,
            .progress-page,
            .admin-page,
            .parent-dashboard,
            .games-path-cards,
            .main-content,
            body {
              background: #ffffff !important;
            }

            /* Apply to any legacy gradient backgrounds */
            [style*="linear-gradient(135deg, #e3f4fd"] {
              background: #ffffff !important;
            }
          ` : ''}

          @keyframes logoutDropdownSlide {
            0% {
              opacity: 0;
              transform: translateY(-10px) scale(0.95);
            }
            100% {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }

          @keyframes museBounce {
            0% { transform: scale(1) rotate(0deg); }
            25% { transform: scale(1.2) rotate(5deg); }
            50% { transform: scale(1.15) rotate(10deg); }
            75% { transform: scale(1.1) rotate(8deg); }
            100% { transform: scale(1) rotate(0deg); }
          }

          @keyframes museImageSpin {
            0% { transform: rotate(0deg) scale(1); }
            25% { transform: rotate(90deg) scale(1.1); }
            50% { transform: rotate(180deg) scale(1.05); }
            75% { transform: rotate(270deg) scale(1.1); }
            100% { transform: rotate(360deg) scale(1); }
          }

          @keyframes museEmojiDance {
            0% { transform: scale(1) rotate(0deg); }
            20% { transform: scale(1.3) rotate(-10deg); }
            40% { transform: scale(1.1) rotate(10deg); }
            60% { transform: scale(1.2) rotate(-5deg); }
            80% { transform: scale(1.05) rotate(5deg); }
            100% { transform: scale(1) rotate(0deg); }
          }

          @keyframes museSparkle0 {
            0% {
              transform: translate(-50%, -50%) scale(0) rotate(0deg);
              opacity: 1;
            }
            25% {
              transform: translate(-60px, -60px) scale(1.5) rotate(180deg);
              opacity: 1;
            }
            100% {
              transform: translate(-100px, -100px) scale(0) rotate(720deg);
              opacity: 0;
            }
          }

          @keyframes museSparkle1 {
            0% {
              transform: translate(-50%, -50%) scale(0) rotate(0deg);
              opacity: 1;
            }
            25% {
              transform: translate(60px, -60px) scale(1.5) rotate(-180deg);
              opacity: 1;
            }
            100% {
              transform: translate(100px, -100px) scale(0) rotate(-720deg);
              opacity: 0;
            }
          }

          @keyframes museSparkle2 {
            0% {
              transform: translate(-50%, -50%) scale(0) rotate(0deg);
              opacity: 1;
            }
            25% {
              transform: translate(-60px, 60px) scale(1.5) rotate(270deg);
              opacity: 1;
            }
            100% {
              transform: translate(-100px, 100px) scale(0) rotate(1080deg);
              opacity: 0;
            }
          }

          @keyframes museSparkle3 {
            0% {
              transform: translate(-50%, -50%) scale(0) rotate(0deg);
              opacity: 1;
            }
            25% {
              transform: translate(60px, 60px) scale(1.5) rotate(-270deg);
              opacity: 1;
            }
            100% {
              transform: translate(100px, 100px) scale(0) rotate(-1080deg);
              opacity: 0;
            }
          }

          @keyframes snowFall {
            0% {
              transform: translateY(0);
            }
            100% {
              transform: translateY(120vh);
            }
          }
        `}</style>
      </div>
    </div>
  );
};

// Split demo and prod routing so /demo is isolated from AuthProvider
const ProdAppRoutes: React.FC = () => (
  <Routes>
    <Route path="/" element={<LoginPage />} />
    <Route path="/claim-sub" element={<ClaimSubLandingPage />} />
    <Route path="/auth/action" element={<AuthActionPage />} />
    <Route path="/student-launch" element={<StudentLaunchPage />} />
    <Route path="/offer" element={<OfferAcceptPage />} />
    <Route path="/library/*" element={<LibraryPage />} />
    <Route
      path="/app"
      element={
        <ProtectedRoute>
          <Layout>
            <TeacherStaffGate>
              <PartnerGate>
                <Outlet />
              </PartnerGate>
            </TeacherStaffGate>
          </Layout>
        </ProtectedRoute>
      }
    >
      {/* Games collection and in-app game routes */}
      <Route path="games" element={<GamesPage />} />
      <Route path="games/:gameId" element={<GamesPage />} />
      <Route path="library/*" element={<LibraryPage />} />

      <Route path="home" element={<StaffHomePage />} />
      <Route path="onboarding" element={<TeacherOnboardingPage />} />
      <Route path="offer" element={<OfferAcceptPage />} />
      <Route path="resources" element={<ResourcesPage />} />
      <Route path="resources/:certificationId" element={<CertificationDetailPage />} />
      <Route path="resources/training/:trainingId" element={<TrainingDetailPage />} />
      <Route path="songs" element={<SavedSongsPage />} />
      <Route path="market" element={<MuseMarketPage />} />
      <Route path="mymuses" element={<MyMusesPage />} />
      <Route path="pack" element={<PackOpeningPage />} />
      <Route
        path="squads"
        element={
          <TeacherProtectedRoute>
            <SquadsPage />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="squads/:squadId"
        element={
          <TeacherProtectedRoute>
            <SquadDashboardPage />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="squads/:squadId/details"
        element={
          <TeacherProtectedRoute>
            <SquadDetailsPage />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="squads/:squadId/mission/:levelId/:missionId"
        element={
          <TeacherProtectedRoute>
            <MissionDashboardPage />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="og-create/:squadId"
        element={
          <TeacherProtectedRoute>
            <OGCreateTeacherPage />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="availability"
        element={
          <TeacherProtectedRoute>
            <AvailabilityPage />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="claim-sub"
        element={
          <TeacherProtectedRoute>
            <ClaimSubPage />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="profile"
        element={
          <TeacherProtectedRoute>
            <TeacherProfilePage />
          </TeacherProtectedRoute>
        }
      />
      <Route
        path="admin"
        element={
          <AdminProtectedRoute allowDelegated>
            <AdminPanelPage />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="admin/squads/:squadId"
        element={
          <AdminProtectedRoute>
            <AdminSquadDetailsPage />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="admin/partners"
        element={
          <AdminProtectedRoute>
            <AdminPartnersPage />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="admin/partners/:partnerId"
        element={
          <AdminProtectedRoute>
            <AdminPartnerDetailPage />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="admin/ops/*"
        element={
          <AdminProtectedRoute>
            <AdminOpsPage />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="admin/billing/:schoolId"
        element={<Navigate to="/app/admin" replace state={{ adminTab: 'invoicing' }} />}
      />
      <Route
        path="admin/invoices/:invoiceId"
        element={
          <AdminProtectedRoute>
            <AdminInvoicePage />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="admin/invoice-template"
        element={
          <AdminProtectedRoute>
            <AdminInvoiceTemplatePage />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="admin/hiring-templates"
        element={
          <AdminProtectedRoute>
            <AdminHiringTemplatesPage />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="admin/referred-schools"
        element={
          <AdminProtectedRoute>
            <AdminReferredSchoolsPage />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="admin/record-payment"
        element={
          <AdminProtectedRoute>
            <AdminRecordPaymentPage />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="admin/teacher-availability"
        element={
          <AdminProtectedRoute>
            <AdminTeacherAvailabilityPage />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="admin/teacher-map"
        element={
          <AdminProtectedRoute>
            <AdminTeacherMapPage />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="admin/teachers/:teacherId"
        element={
          <AdminProtectedRoute>
            <AdminTeacherDetailPage />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="admin/parents/:parentId"
        element={
          <AdminProtectedRoute>
            <AdminParentPreviewPage />
          </AdminProtectedRoute>
        }
      />
      <Route
        path="students"
        element={
          <ParentProtectedRoute>
            <ParentDashboardPage />
          </ParentProtectedRoute>
        }
      />
      <Route index element={<AppIndexRedirect />} />
    </Route>
    <Route path="/pick" element={<ProtectedRoute><PickMusePage /></ProtectedRoute>} />
    <Route path="/parent/create-account" element={<ParentCreateAccount />} />
    <Route path="/legal/terms" element={<Terms />} />
    <Route path="/legal/photo-release" element={<PhotoRelease />} />
    <Route path="/billing" element={<BillingPortalPage />} />
    <Route path="/squads/code/:code" element={<TeacherProtectedRoute><FullScreenCodePage /></TeacherProtectedRoute>} />
    <Route path="/play/:gameId" element={<ProtectedRoute><FullScreenGamePage /></ProtectedRoute>} />
    <Route path="/dashboard" element={<TeacherProtectedRoute><Navigate to="/app/squads" /></TeacherProtectedRoute>} />
    <Route path="*" element={<Navigate to="/" />} />
  </Routes>
);

const App: React.FC = () => {
  return (
    <Router>
      <Routes>
        {/* DEMO: isolated from AuthProvider */}
        <Route path="/demo/*" element={<DemoBoot />} />
        {/* PROD: wrapped in AuthProvider and ThemeProvider */}
        <Route
          path="/*"
          element={
            <ThemeProvider>
              <AuthProvider>
                <TeacherToolsProvider>
                  <ProdAppRoutes />
                </TeacherToolsProvider>
              </AuthProvider>
            </ThemeProvider>
          }
        />
      </Routes>
    </Router>
  );
};

export default App;
